import { type FirebaseApp, initializeApp } from "firebase/app";
import {
  type Auth,
  GoogleAuthProvider,
  type User,
  type UserCredential,
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
} from "firebase/auth";

// Firebase config - values come from VITE_FIREBASE_* env (Firebase Console >
// Project Settings > General > Your apps > Web app). The web app verifies these
// at runtime; the backend (apps/api) verifies the resulting ID token server-side.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let app: FirebaseApp;
let auth: Auth;
let googleProvider: GoogleAuthProvider;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  googleProvider = new GoogleAuthProvider();
  googleProvider.addScope("email");
  googleProvider.addScope("profile");
  googleProvider.setCustomParameters({ prompt: "select_account" });
} catch (error) {
  console.error("Firebase initialization error:", error);
  throw new Error("Failed to initialize Firebase");
}

export { app, auth };
export type { User };

export const firebaseAuth = {
  signInWithEmail: (email: string, password: string): Promise<UserCredential> =>
    signInWithEmailAndPassword(auth, email, password),
  signInWithGoogle: (): Promise<UserCredential> => signInWithPopup(auth, googleProvider),
  signOut: (): Promise<void> => signOut(auth),
  getCurrentUser: (): User | null => auth.currentUser,
  updateDisplayName: async (name: string): Promise<void> => {
    const user = auth.currentUser;
    if (!user) throw new Error("로그인이 필요합니다");
    await updateProfile(user, { displayName: name });
  },
  getIdToken: async (): Promise<string | null> => {
    const user = auth.currentUser;
    if (!user) return null;
    return user.getIdToken();
  },
  onChange: (cb: (user: User | null) => void) => onAuthStateChanged(auth, cb),
};
